import maxBy from 'lodash/maxBy';
import minBy from 'lodash/minBy';

import { configs } from '@hero/configs';
import { SECONDARY_PRODUCT_TYPES } from '@hero/constants';
import { useGetUser } from '@hero/services/organization-service/hooks';
import { getFormattedPrice, storage } from '@hero/utils';

export function useCreatePriceLabel(product) {
  const { data: user } = useGetUser();
  const organizationCode = storage.getOrganizationCode();
  const organization = user?.organizations?.find(o => o.code === organizationCode);

  const isCAActMustBeApplied =
    organization?.configs?.CAActMustBeApplied &&
    !SECONDARY_PRODUCT_TYPES.includes(product?.meta?.productType);

  const maxPrice = isCAActMustBeApplied
    ? product.prices?.maxBreakdown?.display
    : product.prices?.max;
  const minPrice = isCAActMustBeApplied
    ? product.prices?.minBreakdown?.display
    : product.prices?.min;
  const regularMaxPrice = isCAActMustBeApplied
    ? product.regularPrices?.maxBreakdown?.display
    : product.regularPrices?.max;
  const regularMinPrice = isCAActMustBeApplied
    ? product.regularPrices?.minBreakdown?.display
    : product.regularPrices?.min;

  const priceLabel = getFormattedPrice({
    min: minPrice,
    max: maxPrice
  });
  const merchandiseLabel = getFormattedPrice({
    min: product.regularPrices?.minBreakdown?.merchandise,
    max: product.regularPrices?.maxBreakdown?.merchandise
  });
  const serviceChargeLabel = getFormattedPrice({
    min: product.regularPrices?.minBreakdown?.serviceCharge,
    max: product.regularPrices?.maxBreakdown?.serviceCharge
  });
  const regularPriceLabel =
    maxPrice < regularMaxPrice &&
    minPrice < regularMinPrice &&
    maxPrice === minPrice &&
    getFormattedPrice({
      min: regularMinPrice,
      max: regularMaxPrice
    });

  const maxSkuPrice = isCAActMustBeApplied
    ? maxBy(product?.skus, 'priceBreakdown.display')?.priceBreakdown?.display
    : maxBy(product?.skus, 'price')?.price;

  const minSkuPrice = isCAActMustBeApplied
    ? minBy(product?.skus, 'priceBreakdown.display')?.priceBreakdown?.display
    : minBy(product?.skus, 'price')?.price;

  const skusPriceLabel = getFormattedPrice({
    min: minSkuPrice,
    max: maxSkuPrice
  });

  const maxSkuMerchandisePrice = isCAActMustBeApplied
    ? maxBy(product?.skus, 'priceBreakdown.merchandise')?.priceBreakdown?.merchandise
    : maxBy(product?.skus, 'price')?.price;

  const minSkuMerchandisePrice = isCAActMustBeApplied
    ? minBy(product?.skus, 'priceBreakdown.merchandise')?.priceBreakdown?.merchandise
    : minBy(product?.skus, 'price')?.price;

  const skusMerchandisePriceLabel = getFormattedPrice({
    min: minSkuMerchandisePrice,
    max: maxSkuMerchandisePrice
  });

  const maxSkuServiceChargePrice = isCAActMustBeApplied
    ? maxBy(product?.skus, 'priceBreakdown.serviceCharge')?.priceBreakdown?.serviceCharge
    : maxBy(product?.skus, 'price')?.price;

  const minSkuServiceChargePrice = isCAActMustBeApplied
    ? minBy(product?.skus, 'priceBreakdown.serviceCharge')?.priceBreakdown?.serviceCharge
    : minBy(product?.skus, 'price')?.price;

  const skusServiceChargePriceLabel = getFormattedPrice({
    min: minSkuServiceChargePrice,
    max: maxSkuServiceChargePrice
  });

  return {
    isCAActMustBeApplied,
    priceLabel,
    merchandiseLabel,
    serviceChargeLabel,
    minPrice,
    maxPrice,
    maxSkuPrice,
    minSkuPrice,
    skusPriceLabel,
    skusMerchandisePriceLabel,
    skusServiceChargePriceLabel,
    regularPriceLabel: configs?.general?.showDiscountedPrice ? regularPriceLabel : undefined,
    regularMaxPrice,
    regularMinPrice
  };
}
